import React, { useMemo, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { BaseCalendarProps, CalendarEvent } from './types';
import { EventChip } from './EventChip';
import { getWeekdayLabels } from '@/lib/localeFormatters';

export interface MonthViewProps extends BaseCalendarProps {
  month?: Date; // any date within the month
  firstDayOfWeek?: 0 | 1; // 0=Sun, 1=Mon
  maxVisibleEventsPerDay?: number;
  /** Optional custom renderer for each calendar event chip. */
  eventRenderer?: (event: CalendarEvent) => React.ReactNode;
}

function startOfMonth(date: Date) {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, days: number) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isSameMonth(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

function getMonthGrid(date: Date, firstDayOfWeek: 0 | 1) {
  const start = startOfMonth(date);
  const startWeekday = (start.getDay() + 7 - firstDayOfWeek) % 7;
  const gridStart = addDays(start, -startWeekday);
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) {
    days.push(addDays(gridStart, i));
  }
  return days;
}

function eventsForDay(events: CalendarEvent[] | undefined, day: Date) {
  if (!events) return [] as CalendarEvent[];
  return events.filter((e) => e.start <= addDays(day, 1) && e.end >= day);
}

export const MonthView: React.FC<MonthViewProps> = ({
  month = new Date(),
  firstDayOfWeek = 1,
  events,
  onEventClick,
  onDayClick,
  maxVisibleEventsPerDay = 3,
  eventRenderer,
}) => {
  const days = useMemo(() => getMonthGrid(month, firstDayOfWeek), [month, firstDayOfWeek]);
  const WEEKDAY_LABELS = useMemo(() => getWeekdayLabels(), []);
  const today = new Date();

  return (
    <div className="w-full">
      {/* Weekday header */}
      <div className="grid grid-cols-7 text-xs text-muted-foreground mb-2">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="px-2 py-2">{WEEKDAY_LABELS[(i + firstDayOfWeek) % 7]}</div>
        ))}
      </div>

      <div role="grid" aria-label="Monthly calendar" className="grid grid-cols-7 gap-2">
        {days.map((day, idx) => {
          const inMonth = isSameMonth(day, month);
          const isToday = isSameDay(day, today);
          const dayEvents = eventsForDay(events, day).sort((a, b) => a.start.getTime() - b.start.getTime());
          const visible = dayEvents.slice(0, maxVisibleEventsPerDay);
          const overflow = Math.max(0, dayEvents.length - visible.length);
          return (
            <div
              key={idx}
              role="gridcell"
              aria-selected={isToday}
              className={cn(
                'border rounded-lg p-2 min-h-[100px] bg-card focus-within:ring-2 focus-within:ring-primary-500 outline-none',
                !inMonth && 'bg-muted text-muted-foreground',
                isToday && 'border-primary-400',
                onDayClick && 'cursor-pointer hover:bg-accent/20',
              )}
              tabIndex={0}
              onClick={onDayClick ? () => onDayClick(day) : undefined}
              onKeyDown={onDayClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onDayClick(day); } : undefined}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={cn('text-sm font-medium', isToday && 'text-primary-700')}>{day.getDate()}</span>
              </div>
              <div className="space-y-1">
                {visible.map((ev) => (
                  eventRenderer
                    ? <div key={ev.id} onClick={(e) => e.stopPropagation()}>{eventRenderer(ev)}</div>
                    : <EventChip key={ev.id} event={ev} onClick={onEventClick} />
                ))}
                {overflow > 0 && (
                  <div className="text-[11px] text-muted-foreground">+{overflow} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


